import React, { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Check, RotateCcw } from "lucide-react";
import { AssistantBar, TypingText, ChatBubble, DemoChip } from "@/components/fittin/mcpShared";
import { ASSISTANTS, B2C_QUESTIONS } from "@/lib/mcpDemoContent";

// B2C MCP LIVE DEMO (mock — no real LLM).
// The shopper's own assistant answers fit questions using their FitID. Selecting
// a suggested question streams a pre-written, illustrative answer into an
// LLM-style chat bubble. All copy lives in src/lib/mcpDemoContent.js.
export default function ConsumerMCP() {
  const [assistant, setAssistant] = useState("claude");
  const [messages, setMessages] = useState([]); // {key, role, text}
  const [usedIds, setUsedIds] = useState([]);

  const activeLabel = ASSISTANTS.find((a) => a.key === assistant)?.label || "Assistant";

  const ask = (question) => {
    if (usedIds.includes(question.id)) return;
    setUsedIds((ids) => [...ids, question.id]);
    setMessages((m) => [
      ...m,
      { key: `${question.id}-u`, role: "user", text: question.q },
      { key: `${question.id}-a`, role: "assistant", text: question.a },
    ]);
  };

  const reset = () => {
    setMessages([]);
    setUsedIds([]);
  };

  return (
    <section id="mcp-teaser" data-testid="mcp-teaser-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1 text-[10.5px] tracking-[0.22em] uppercase text-neutral-500">
            <Sparkles className="h-3 w-3" />
            MCP · Live demo
          </div>
          <h2
            data-testid="mcp-teaser-headline"
            className="mt-5 text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] font-medium leading-[1.02]"
          >
            Your FitKey, <span className="text-neutral-400">in your AI assistant.</span>
          </h2>
          <p className="mt-5 max-w-lg text-[15.5px] text-neutral-600 leading-relaxed">
            Connect the same FitKey that lives in your Wallet to Claude, ChatGPT, Gemini, or any
            MCP-ready assistant. Ask about any shoe in any shop — it answers with your size.
            Try a question on the right to see how it feels.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-lg">
            {[
              "Portable across every shop",
              "Private — assistant reads, doesn't store",
              "Works with existing chats",
            ].map((line, i) => (
              <div
                key={i}
                data-testid={`mcp-benefit-${i + 1}`}
                className="rounded-xl border border-black/10 bg-white p-3"
              >
                <Check className="h-3.5 w-3.5 text-emerald-600" />
                <div className="mt-2 text-[12px] text-neutral-700 leading-snug">{line}</div>
              </div>
            ))}
          </div>
        </div>

        {/* LLM prompt-window-styled panel */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          data-testid="mcp-demo-card"
          className="lg:col-span-7 rounded-3xl border border-black/10 bg-white overflow-hidden"
        >
          {/* Window chrome */}
          <div className="flex items-center gap-2 px-5 h-11 border-b border-black/5 bg-neutral-50">
            <span className="h-2.5 w-2.5 rounded-full bg-black/10" />
            <span className="h-2.5 w-2.5 rounded-full bg-black/10" />
            <span className="h-2.5 w-2.5 rounded-full bg-black/10" />
            <div className="mx-auto text-[11px] text-neutral-500 font-mono">
              {activeLabel.toLowerCase()} · Fittin MCP
            </div>
            <DemoChip testid="mcp-demo-chip" />
          </div>

          <div className="p-5 md:p-6">
            <div className="flex items-center justify-between gap-3">
              <AssistantBar
                assistants={ASSISTANTS}
                active={assistant}
                onChange={setAssistant}
                testidPrefix="mcp"
              />
              {messages.length > 0 && (
                <button
                  type="button"
                  data-testid="mcp-reset"
                  onClick={reset}
                  className="inline-flex items-center gap-1.5 text-[12px] text-neutral-500 hover:text-[#0A0A0A] transition-colors"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset
                </button>
              )}
            </div>

            {/* Chat area */}
            <div
              data-testid="mcp-chat"
              className="mt-4 min-h-[180px] rounded-2xl bg-[#FAFAFA] border border-black/5 p-4 space-y-3"
            >
              {messages.length === 0 ? (
                <div className="h-full min-h-[150px] grid place-items-center text-center">
                  <div className="text-[13px] text-neutral-400 max-w-[240px]">
                    Pick a question below. Your assistant will answer using your FitKey
                    (foot length 26.2 cm).
                  </div>
                </div>
              ) : (
                messages.map((m) => (
                  <ChatBubble key={m.key} role={m.role} testid={`mcp-bubble-${m.key}`}>
                    {m.role === "assistant" ? <TypingText text={m.text} /> : m.text}
                  </ChatBubble>
                ))
              )}
            </div>

            {/* Selectable example questions */}
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                Example questions
              </div>
              <div className="mt-2 space-y-2">
                {B2C_QUESTIONS.map((question) => {
                  const used = usedIds.includes(question.id);
                  return (
                    <button
                      key={question.id}
                      type="button"
                      disabled={used}
                      data-testid={`mcp-question-${question.id}`}
                      onClick={() => ask(question)}
                      className={`flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left text-[12.5px] transition-all ${
                        used
                          ? "border-emerald-200 bg-emerald-50/50 text-neutral-400"
                          : "border-black/10 bg-white hover:bg-neutral-50 text-neutral-700"
                      }`}
                    >
                      <span
                        className={`mt-0.5 grid place-items-center h-4 w-4 shrink-0 rounded border ${
                          used
                            ? "bg-emerald-500 border-emerald-500"
                            : "border-black/20 bg-white"
                        }`}
                      >
                        {used && <Check className="h-3 w-3 text-white" />}
                      </span>
                      {question.q}
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-[11px] text-neutral-400">
                Illustrative demo — answers are examples, not verified fit facts.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
